import React, { useEffect } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { useTranslation } from "../i18n/LocaleContext";
import TenantAdminPanel from "./TenantAdminPanel";
import { AuthorizedFetch } from "../utils/tenantAdminApi";

/** Chamada em curso, como o `callevent`/`presence` do softphone a descreve. */
export type TenantAdminCallState =
  | { kind: "idle" }
  /** Entrante tocando: dá para atender voltando ao softphone. `from` falta no build sem callevent. */
  | { kind: "ringing"; from?: string }
  /** Em chamada, ou ocupado sem detalhe (só `presence: busy`). */
  | { kind: "active"; detailed: boolean };

interface TenantAdminScreenProps {
  authorizedFetch: AuthorizedFetch;
  currentUserId: string;
  onBack: () => void;
  /** Estado da chamada, para a faixa do topo. Ausente onde não há softphone (tela de bloqueio). */
  callState?: TenantAdminCallState;
  /** Leva de volta ao softphone. Ausente quando não há softphone para onde ir. */
  onGoToSoftphone?: () => void;
}

/**
 * Casca da tela de administração de usuários do tenant: título, caminho de volta e a faixa de
 * chamada. Usada nos dois pontos de entrada — dentro do workspace (com o softphone vivo, apenas
 * fora de vista) e a partir da tela de bloqueio, onde não há softphone.
 */
const TenantAdminScreen: React.FC<TenantAdminScreenProps> = ({
  authorizedFetch,
  currentUserId,
  onBack,
  callState = { kind: "idle" },
  onGoToSoftphone,
}) => {
  const { t } = useTranslation();

  // Esc também volta. Só quando nenhum diálogo está aberto: o MUI para a propagação do Esc no
  // Dialog, então um diálogo aberto consome a tecla antes de chegar aqui.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onBack();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onBack]);

  // A chamada toca de todo modo (o áudio do softphone não depende do layout), mas o painel de
  // atendimento está fora de vista e não recebe cliques. A faixa diz o que está acontecendo e
  // leva de volta; a vista nunca troca sozinha, para não interromper uma edição em andamento.
  const callBanner = () => {
    if (callState.kind === "idle" || !onGoToSoftphone) return null;

    const ringing = callState.kind === "ringing";
    const label = ringing
      ? callState.from
        ? t("tenantAdmin.call.ringingFrom", { number: callState.from })
        : t("tenantAdmin.call.unknown")
      : callState.detailed
        ? t("tenantAdmin.call.active")
        : t("tenantAdmin.call.unknown");

    return (
      <div
        className="tenant-admin-call-banner"
        data-ringing={ringing ? "true" : "false"}
        role="status"
        // Interromper o leitor de tela é o comportamento certo para uma chamada entrante, e só
        // para ela.
        aria-live={ringing ? "assertive" : "polite"}
      >
        <span>{label}</span>
        <Button size="small" variant="contained" onClick={onGoToSoftphone}>
          {ringing ? t("tenantAdmin.call.answer") : t("tenantAdmin.call.backToSoftphone")}
        </Button>
      </div>
    );
  };

  return (
    <section className="tenant-admin-screen">
      {callBanner()}
      <header className="tenant-admin-screen-header">
        <IconButton size="small" aria-label={t("tenantAdmin.back")} onClick={onBack}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <h2>{t("tenantAdmin.title")}</h2>
      </header>
      <TenantAdminPanel authorizedFetch={authorizedFetch} currentUserId={currentUserId} />
    </section>
  );
};

export default TenantAdminScreen;
